import { observable, action, makeObservable, runInAction, reaction } from 'mobx'
import { exchangePin } from '../Services/AuthorizationService'
import { extractAllowedActions } from '../Utils/jwt'
import {
  KdsAuthorizationCancelledError,
  KdsAuthorizationError,
  KdsAuthorizationErrorReason,
  KdsStepUpAuthorization,
  PinExchangeFailure,
} from '../Types/Authorization'
import { KdsActionId, KdsActionPolicy } from '../Types/KdsAuthorization'
import { isActionAvailable, isActionElevated } from '../Utils/kdsActionPolicy'
import GlobalStore from './GlobalStore'

// Comfortably covers the round trip to the protected endpoint the token is used against,
// so a token that's about to expire doesn't pass preflight only to be rejected server-side.
const PREFLIGHT_SAFETY_MARGIN_MS = 5000

function cooldownError(cooldownUntil: number, now = Date.now()): KdsAuthorizationError {
  const remainingMilliseconds = cooldownUntil - now
  const roundedUpSeconds = Math.ceil(remainingMilliseconds / 1000)

  return {
    reason: KdsAuthorizationErrorReason.Cooldown,
    retryAfterSeconds: Math.max(1, roundedUpSeconds),
  }
}

function preflight(accessToken: string, expiresIn: number): KdsStepUpAuthorization | undefined {
  const now = Date.now()
  const expiresAt = now + expiresIn * 1000 - PREFLIGHT_SAFETY_MARGIN_MS
  if (expiresAt <= now) return undefined

  const allowedActions = extractAllowedActions(accessToken)

  return { token: accessToken, expiresAt, allowedActions }
}

class KdsAuthorizationStore {
  pendingActionId: KdsActionId | undefined = undefined

  submitting = false

  error: KdsAuthorizationError | undefined = undefined

  cooldownUntil = 0

  // True private field: never observable, and never enumerable on the instance, since the
  // callback closes over caller state and, transiently, the raw token.
  #pending:
    | {
        callback: (authorization: KdsStepUpAuthorization) => Promise<unknown>
        resolve: (value: unknown) => void
        reject: (reason?: unknown) => void
      }
    | undefined

  #disposeRevokedReaction: () => void

  constructor() {
    makeObservable(this, {
      pendingActionId: observable,
      submitting: observable,
      error: observable,
      cooldownUntil: observable,
      authorizeAndRun: action,
      submitPin: action,
      expireCooldown: action,
      cancelPending: action,
      reset: action,
    })

    this.#disposeRevokedReaction = reaction(
      () => GlobalStore.deviceRevoked,
      revoked => {
        if (revoked) this.reset()
      }
    )
  }

  // The singleton lives for the app's lifetime and never needs this; it exists so tests
  // that spin up throwaway instances can release the deviceRevoked reaction.
  dispose() {
    this.#disposeRevokedReaction()
  }

  // Opens the prompt with no policy gate -- trusts the caller to have gated the action.
  // Use the policy-gated entry points (authorizeConfiguredAction / runConfiguredAction) instead.
  authorizeAndRun<T>(
    actionId: KdsActionId,
    callback: (authorization: KdsStepUpAuthorization) => Promise<T>
  ): Promise<T> {
    if (this.pendingActionId) {
      return Promise.reject(new Error('An authorization is already pending'))
    }

    this.expireCooldown()

    return new Promise<T>((resolve, reject) => {
      this.#pending = {
        callback: callback as (authorization: KdsStepUpAuthorization) => Promise<unknown>,
        resolve: resolve as (value: unknown) => void,
        reject,
      }

      this.pendingActionId = actionId
      this.submitting = false
      this.error = this.cooldownUntil > Date.now() ? cooldownError(this.cooldownUntil) : undefined
    })
  }

  // Resolves undefined on the device-token path (action available but not elevated); resolves
  // the step-up authorization once the operator's PIN is accepted on the elevated path.
  authorizeConfiguredAction(actionId: KdsActionId): Promise<KdsStepUpAuthorization | undefined> {
    return this.authorizeConfiguredActions([actionId])
  }

  // Multi-action variant for writes that need several permissions at once (e.g. a Busy write
  // needs both SetDepartmentAvailability and AdjustDepartmentPrepTime). Available only when every
  // action is; elevated when any is. Prompts on the first elevated action -- a single step-up token
  // carries all of the operator's permissions, so it satisfies the whole write server-side.
  authorizeConfiguredActions(
    actionIds: readonly KdsActionId[]
  ): Promise<KdsStepUpAuthorization | undefined> {
    const policy: KdsActionPolicy | undefined = GlobalStore.configFetch.data?.actionPolicy

    const unavailable = actionIds.find(actionId => !isActionAvailable(policy, actionId))
    if (unavailable) {
      return Promise.reject(new Error(`Action is not available: ${unavailable}`))
    }

    const elevated = actionIds.find(actionId => isActionElevated(policy, actionId))
    if (!elevated) {
      return Promise.resolve(undefined)
    }

    return this.authorizeAndRun(elevated, async authorization => authorization)
  }

  runConfiguredAction<T>(
    actionId: KdsActionId,
    deviceOperation: () => Promise<T>,
    elevatedOperation: (authorization: KdsStepUpAuthorization) => Promise<T>
  ): Promise<T> {
    return this.runConfiguredActions([actionId], deviceOperation, elevatedOperation)
  }

  async runConfiguredActions<T>(
    actionIds: readonly KdsActionId[],
    deviceOperation: () => Promise<T>,
    elevatedOperation: (authorization: KdsStepUpAuthorization) => Promise<T>
  ): Promise<T> {
    const authorization = await this.authorizeConfiguredActions(actionIds)
    return authorization ? elevatedOperation(authorization) : deviceOperation()
  }

  async submitPin(pin: string): Promise<void> {
    if (!this.pendingActionId || this.submitting) return

    this.expireCooldown()
    if (this.cooldownUntil > Date.now()) {
      this.error = cooldownError(this.cooldownUntil)
      return
    }

    const actionId = this.pendingActionId
    // Identity of the operation this submit belongs to. #pending is a fresh object per
    // authorizeAndRun, so it distinguishes a torn-down-and-restarted operation that reuses
    // the same action id -- which an action-id comparison alone cannot.
    const pendingAtSubmit = this.#pending
    runInAction(() => {
      this.submitting = true
      this.error = undefined
    })

    const result = await exchangePin(pin)

    // Cancelled or reset mid-flight. The keypad is disabled while submitting, so no competing
    // submit can race in here.
    if (this.#pending !== pendingAtSubmit) return

    if (result.outcome !== 'success') {
      runInAction(() => {
        this.submitting = false
        this.error = this.recordFailedExchange(result)
      })
      return
    }

    runInAction(() => {
      this.clearAttemptState()
    })

    const authorization = preflight(result.accessToken, result.expiresIn)
    if (!authorization) {
      // Preflight failure is a terminal reject -- the token is structurally unusable.
      this.rejectAndClear(new Error('Step-up token failed local preflight'))
      return
    }

    if (authorization.allowedActions?.includes(actionId) === false) {
      runInAction(() => {
        this.submitting = false
        this.error = { reason: KdsAuthorizationErrorReason.IneligibleOperator }
      })
      return
    }

    const pending = this.clearPendingState()
    if (!pending) return

    try {
      pending.resolve(await pending.callback(authorization))
    } catch (error) {
      pending.reject(error)
    }
  }

  cancelPending(reason?: string) {
    if (!this.pendingActionId) return

    this.error = { reason: KdsAuthorizationErrorReason.Cancelled }
    this.rejectAndClear(new KdsAuthorizationCancelledError(reason ?? 'cancelled'))
  }

  expireCooldown() {
    if (this.cooldownUntil === 0 || this.cooldownUntil > Date.now()) return

    this.clearAttemptState()
    if (this.error?.reason === KdsAuthorizationErrorReason.Cooldown) {
      this.error = undefined
    }
  }

  reset() {
    this.rejectAndClear(new KdsAuthorizationCancelledError('reset'))
    this.error = undefined
    this.clearAttemptState()
  }

  // submitPin bails while a cooldown is active, so none is in effect when this runs.
  private recordFailedExchange(result: PinExchangeFailure): KdsAuthorizationError {
    const now = Date.now()

    if (result.outcome === 'cooldown') {
      this.cooldownUntil = now + result.retryAfterSeconds * 1000
      return cooldownError(this.cooldownUntil, now)
    }

    if (result.outcome === 'invalidPin') {
      return { reason: KdsAuthorizationErrorReason.InvalidPin }
    }

    return {
      reason:
        result.outcome === 'ineligibleOperator'
          ? KdsAuthorizationErrorReason.IneligibleOperator
          : KdsAuthorizationErrorReason.TransportError,
    }
  }

  private clearAttemptState() {
    this.cooldownUntil = 0
  }

  private rejectAndClear(error: unknown) {
    this.clearPendingState()?.reject(error)
  }

  // Both submitPin's success path and rejectAndClear tear down the same pending state --
  // centralised here so the reset stays in one place regardless of outcome.
  private clearPendingState() {
    const pending = this.#pending
    this.#pending = undefined

    runInAction(() => {
      this.pendingActionId = undefined
      this.submitting = false
    })

    return pending
  }
}

const kdsAuthorizationStore = new KdsAuthorizationStore()
export default kdsAuthorizationStore
export { KdsAuthorizationStore }
